import React from 'react';

// Throwaway prototype exploring a huggingface.co inspired visual style —
// white background, thin borders, dense flat card grids, minimal colour —
// applied to the same content as the real machine-learning-landing.js page,
// without touching that page or its data. See default.scss for the .hf-*
// styling this relies on.

const SECTIONS = [
    {
        title: 'Machine Learning Basics',
        prefix: '/machine-learning/basics/',
        items: [
            {title: 'What is Machine Learning?', content: 'An introduction to machine learning and what it can do.', slug: 'what-is-machine-learning'},
            {title: 'Brief history of Machine Learning', content: 'How machine learning has evolved over the decades', slug: 'brief-history-of-machine-learning'},
            {title: 'How do machines learn?', content: 'Some of the concepts behind machine learning', slug: 'how-do-machines-learn'},
            {title: 'Common Machine Learning Frameworks', content: 'A brief examination of some common Machine Learning frameworks and tools', slug: 'machine-learning-frameworks'},
        ]
    },
    {
        title: 'Concepts',
        prefix: '/machine-learning/concepts/',
        items: [
            {title: 'Types of Neural Networks', content: 'Some of the most common types of Neural Networks', slug: 'types-of-neural-networks'},
            {title: 'Common Algorithms', content: 'Some of the algorithms that power Machine Learning', slug: 'common-machine-learning-algorithms'},
            {title: 'Data and Feature Analysis', content: 'Introduction to data cleansing and feature engineering', slug: 'data-and-feature-engineering'},
            {title: 'Large Data Sets', content: 'Dealing with Large Data Sets', slug: 'large-data-sets'},
            {title: 'Data Bias', content: 'Data bias and how it manifests', slug: 'data-bias-in-machine-learning'},
            {title: 'When not to use Machine Learning', content: 'Inferred learning is not always the correct approach', slug: 'when-to-not-use-machine-learning'},
        ]
    },
    {
        title: 'Classification & Unsupervised Learning',
        prefix: '/machine-learning/classification/',
        items: [
            {title: 'Binary Classification', content: 'Classification and common problems/approaches', slug: 'binary-classification'},
            {title: 'Clustering', content: 'Grouping related items', slug: 'clustering'},
            {title: 'Optimization', content: 'Searching for the best fit without labelled data', slug: 'machine-learning-optimization'},
            {title: 'Simulation', content: 'Generating data and environments to learn from', slug: 'unsupervised-learning-simulation'},
        ]
    },
    {
        title: 'Regression',
        prefix: '/machine-learning/regression/',
        items: [
            {title: 'What is Regression', content: 'Predicting continuous values from data', slug: 'basic-regression'},
            {title: 'A Basic Regression Problem', content: 'Working through a simple regression problem', slug: 'basic-regression-problem'},
        ]
    },
    {
        title: 'Image Processing',
        prefix: '/machine-learning/concepts/',
        items: [
            {title: 'Image processing in ML', content: 'How are images processed in Machine Learning', slug: 'basic-image-processing'},
            {title: 'Convolutional Neural Networks (CNNs)', content: 'How do CNNs work?', slug: 'convolutional-neural-network'},
        ]
    },
    {
        title: 'Natural Language Processing',
        prefix: '/machine-learning/concepts/',
        items: [
            {title: 'Natural Language Processing', content: 'Processing and classifying text', slug: 'basic-natural-language-processing'},
            {title: 'Large Language Models (LLMs)', content: 'Dealing with Large Language Models', slug: 'large-language-models'},
        ]
    },
];

const totalPages = SECTIONS.reduce((sum, s) => sum + s.items.length, 0);

const HFStylePreview = () => (
    <div className="hf-page min-h-screen">
        <div className="w-full px-4 max-w-6xl mx-auto py-12">

            <div className="text-center mb-14">
                <p className="hf-eyebrow">{SECTIONS.length} sections &middot; {totalPages} pages</p>
            </div>

            <div id="sections">
                {SECTIONS.map(section => (
                    <div key={section.title} className="mb-14">
                        <div className="flex items-baseline justify-between mb-4">
                            <h2 className="text-xl font-bold">{section.title}</h2>
                            <span className="text-sm text-gray-400">{section.items.length} pages</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {section.items.map(item => (
                                <a key={item.slug} href={section.prefix + item.slug} className="hf-card block p-4">
                                    <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                                    <p className="text-xs text-gray-500 mb-3">{item.content}</p>
                                    <span className="hf-pill">{item.slug}</span>
                                </a>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

        </div>
    </div>
);

export default HFStylePreview;
